import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nContext';
import { orderPriceRange, parsePriceInput } from '../utils/priceInput';

interface PriceFilterProps {
  min?: number;
  max?: number;
  /** Cheapest and dearest product available; shown as hints in the empty fields. */
  rangeMin?: number;
  rangeMax?: number;
  onChange: (min?: number, max?: number) => void;
}

// 16px below lg on purpose: iOS Safari zooms the page when a smaller field gets focus.
const FIELD_CLASS = [
  'w-0 flex-1 min-w-0 h-9 lg:h-8 px-2 text-base lg:text-sm text-[#1a1a1a] bg-white',
  'border border-gray-300 rounded-[2px] placeholder:text-gray-400',
  'focus:outline focus:outline-2 focus:outline-brand',
].join(' ');

/** Applied on Enter, or when focus leaves both fields. */
export const PriceFilter: React.FC<PriceFilterProps> = ({ min, max, rangeMin, rangeMax, onChange }) => {
  const { t, lang } = useI18n();
  const format = useCallback(
    (value?: number) => (value === undefined ? '' : lang === 'el' ? String(value).replace('.', ',') : String(value)),
    [lang],
  );

  const [from, setFrom] = useState(format(min));
  const [to, setTo] = useState(format(max));
  const groupRef = useRef<HTMLDivElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setFrom(format(min)); }, [min, format]);
  useEffect(() => { setTo(format(max)); }, [max, format]);
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const commit = () => {
    const parsedFrom = parsePriceInput(from);
    const parsedTo = parsePriceInput(to);
    if (parsedFrom === null) setFrom(format(min));
    if (parsedTo === null) setTo(format(max));
    const [nextMin, nextMax] = orderPriceRange(
      parsedFrom === null ? min : parsedFrom,
      parsedTo === null ? max : parsedTo,
    );
    setFrom(format(nextMin));
    setTo(format(nextMax));
    if (nextMin !== min || nextMax !== max) onChange(nextMin, nextMax);
  };

  // Moving from one field to the other must not start a search. relatedTarget is
  // unreliable on iOS, so look at where focus landed. getRootNode because
  // document.activeElement is the shadow host inside the widget.
  const handleBlur = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
    blurTimer.current = setTimeout(() => {
      const group = groupRef.current;
      if (!group) return;
      const root = group.getRootNode() as Document | ShadowRoot;
      if (!group.contains(root.activeElement)) commit();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    commit();
  };

  return (
    <div ref={groupRef} className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <label className="flex flex-1 items-center gap-1.5 min-w-[88px] max-w-[112px] lg:max-w-[101px] text-xs text-gray-500">
        {t('filter.priceFrom')}
        <input
          type="text"
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          value={from}
          placeholder={rangeMin !== undefined ? `${rangeMin}€` : undefined}
          onChange={(e) => setFrom(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={FIELD_CLASS}
        />
      </label>
      <label className="flex flex-1 items-center gap-1.5 min-w-[88px] max-w-[112px] lg:max-w-[101px] text-xs text-gray-500">
        {t('filter.priceTo')}
        <input
          type="text"
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          value={to}
          placeholder={rangeMax !== undefined ? `${rangeMax}€` : undefined}
          onChange={(e) => setTo(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={FIELD_CLASS}
        />
      </label>
    </div>
  );
};
